import Vue from 'vue';
import Vuex from 'vuex';
import dayjs from 'dayjs';
import http from '@/api/index.js';

Vue.use(Vuex);

// 로그인 상태 체크 함수
// localStorage에 token이 존재하고 access 토큰이 있으면 true, 없으면 false 반환
const loginChk = function () {
  const token = JSON.parse(localStorage.getItem('token'));
  return !!(token && token.access);
};

const store = new Vuex.Store({
  state: {
    year: dayjs().get('year'), // 현재 연도
    currentDate: dayjs().format('YYYY-MM-DD'), // 현재 날짜
    loginProcess: false, // 로그인 처리 중 여부
    user: { // 사용자 정보
      idx: null,
      id: null,
      name: null
    },
    token: { // 토큰 정보
      access: null,
      exp: null,
      refresh: null,
      refreshExp: null
    },
    login: loginChk(), // 로그인 상태 (true/false)
    adMsgAgree: false, // 광고 메시지 수신 동의 여부
    cert: { // 인증 관련 상태
      type: 'phone', // 인증 타입 (phone, email 등)
      info: {} // 인증 관련 추가 정보
    },

    // 시즌 설정 (API 기반)
    seasonType: null,
    seasonMsg: '',
  },

  mutations: {
    // 로그인 체크: 로그인하지 않은 상태면 로그인 페이지로 이동
    loginCheck(state) {
      if (!state.login) {
        this.$router.push('/login');
      }
    },

    // 로그인 처리
    // payload: { state: boolean, token: string }
    async login(state, payload) {
      state.loginProcess = true; // 로그인 진행 상태
      state.login = payload.state; // 로그인 상태
      state.token.access = payload.token; // access 토큰 저장
      // 토큰 만료시간, refresh token 관련 주석 처리됨
      localStorage.setItem('token', JSON.stringify(state.token)); // 로컬스토리지에 토큰 저장
      state.loginProcess = false;

      // 로그인 시 마지막 활동 시간 저장
      const currentTime = new Date().getTime();
      localStorage.setItem('lastActivity', JSON.stringify(currentTime));

      // 사용자 정보 가져오기
      await store.dispatch('getUserInfo');
    },

    // 로그아웃 처리
    logout(state) {
      state.login = false;
      state.token.access = null;
      // 토큰 관련 값 초기화 (exp, refresh 등 주석 처리됨)
      state.user.idx = null;
      state.user.id = null;
      state.user.name = null;

      delete localStorage.token; // 로컬스토리지 토큰 삭제
    },

    // 사용자 정보 업데이트
    userInfo(state, payload) {
      state.user.idx = payload.idx;
      state.user.id = payload.id;
      state.user.name = payload.name;
    },

    // 광고 메시지 수신 동의 상태 업데이트
    adMsgAgree(state, payload) {
      state.adMsgAgree = payload;
    },

    // 인증 정보 업데이트
    setCert(state, payload) {
      state.cert.type = payload.type;
      state.cert.info = payload.info;
    },

    // 시즌 설정 mutation
    setSeasonConfig(state, payload) {
      state.seasonType = payload.seasonType;
      state.seasonMsg = payload.seasonMsg;
    }
  },

  actions: {
    // 로컬스토리지 토큰 유효성 확인 후 로그인 상태 유지
    async tokenCheck({commit}){
      const token = JSON.parse(localStorage.getItem('token'));
      if(token && token.expired) { // 토큰 만료
        commit('logout');
      } else if(token && token.exp >= dayjs().unix()){ // 토큰 유효
        commit('login', {
          state: true,
          accessToken: {
            token: token.access,
            exp: token.exp,
            id: '',
            name: ''
          },
        })

        // 사용자 정보 가져오기
        const user_info = await http.get('/user');
        commit('userInfo', {
          idx: user_info.data.data.idx,
          id: user_info.data.data.user_id,
          name: user_info.data.data.name,
        })
      } else {
        // 토큰 만료 시 로그아웃
        commit('logout')
      }
    },

    // 서버에서 로그인 상태 확인 후 유효하지 않으면 로그아웃
    async loginTokenCheck({ commit }) {
      const {data} = await http.get(`/account/check`);
      if(!data.success) {
        alert('로그아웃 되었습니다.')
        commit('logout');
      }
    },

    // 사용자 정보 가져오기
    async getUserInfo({ commit }) {
      const user_info = await http.get('/user');
      commit('userInfo', {
        idx: user_info.data.data.idx,
        id: user_info.data.data.user_id,
        name: user_info.data.data.name,
      })
      return new Promise((resolve, reject) => {
        resolve(); // Promise 반환
      });
    },

    // 로그아웃 액션 (서버 호출 포함)
    logout({commit}){
      http.post('/account/logout').then(res => {
        if(res.data.success) {
          alert('로그아웃 되었습니다.');
          commit('logout');
          location.replace('/'); // 홈으로 리다이렉트
        }
      }).catch(err => {
        console.log(err);
      });
    },

    // 시즌 설정 API 호출 + 24시간 TTL 캐싱
    async fetchSeasonConfig({ commit }) {
      console.log('fetchSeasonConfig');
      // 임시 데이터
      const seasonData = {
        seasonType: {
          // 메인
          mainYouth: false,
          mainRanking: true,
          // 프로
          player: true,
          staff: true,
          schedule: true,
          // 유스
          littleDragonsClub: true,
          academyStaff: false,
          youthPlayer: true,
          // 티켓
          reservations: false,
          seasonInfo: true,
          // 경기
          matchList: false,
          teamRanking: true,
          playerRanking: true,
          // 기타
          sponsor: true,
        },
        seasonMsg: '26시즌 준비 중입니다.',
      };

      commit('setSeasonConfig', seasonData);

      /*try {
        const { data } = await http.get('/season/config');

        if (data.success) {
          const seasonData = {
            seasonType: data.data.seasonType,
            seasonMsg: data.data.seasonMsg,
          };

          commit('setSeasonConfig', seasonData);
        }
      } catch (error) {
        console.error('시즌 정보 조회 실패:', error);
      }*/
    }
  },

  getters: {
    getUserInfo: state => state.user, // 사용자 정보
    getLoginState: state => state.login, // 로그인 상태
    getAdMsgAgree: state => state.adMsgAgree, // 광고 메시지 동의 여부
    getCertType: state => state.cert.type, // 인증 타입
    getCertInfo: state => state.cert.info, // 인증 정보
    getSeasonType: state => state.seasonType, // 시즌 타입
    getSeasonMsg: state => state.seasonMsg,
  },
})

export default store;
